// Words for a generated screen. A registry setting or a contract field the console has copy for reads
// as that copy; one it has none for reads as its own name, made readable — a new setting still gets
// a usable screen the day it is registered, and its copy can follow.

import { useTranslation } from '@clavian/i18n/react';
import { useMemo } from 'react';

import { humanise } from '../lib/json';
import type { JsonSchema } from './schema';
import type { SettingLabels } from './types';

/** `venue.closingAction` → `venue__closingAction`: i18next reads a dot as nesting. */
export const copyId = (key: string): string => key.replaceAll('.', '__');

/** Only an identifier is made readable; `Asia/Kolkata` or `+05:30` is shown as it is. */
const readable = (value: string): string => (/^[A-Za-z][A-Za-z0-9_]*$/.test(value) ? humanise(value) : value);

export interface LabelRoots {
  /** Copy for a whole entry, by its key (`settings.label`). */
  readonly entry?: string;
  /** Copy for a property, by its name (`settings.field`, `venueProfile.label`). */
  readonly field: string;
  /** What one row of a list is called, by the list's name. */
  readonly item?: string;
  /** A property's description, by its name. Without it, the schema's own is used. */
  readonly description?: string;
  /** An option's label: `<root>.<property or entry>.<value>`. */
  readonly option: string;
}

/**
 * Labels from the copy under `roots`, in namespace `ns`: the console's own by default, HQ's for the
 * brand screen (`@clavian/i18n/hq`).
 */
export function useSettingLabels(roots: LabelRoots, ns: 'console' | 'hq' = 'console'): SettingLabels {
  const { t, i18n } = useTranslation(ns);
  return useMemo(() => {
    const copy = (key: string): string | undefined => (i18n.exists(key, { ns }) ? t(key) : undefined);
    const last = (entryKey: string, path: readonly string[]) => path.at(-1) ?? entryKey;
    return {
      field(entryKey, path) {
        if (path.length === 0) {
          return (
            (roots.entry === undefined ? undefined : copy(`${roots.entry}.${copyId(entryKey)}`)) ??
            copy(`${roots.field}.${entryKey}`) ??
            humanise(entryKey.split('.').at(-1) ?? entryKey)
          );
        }
        const name = last(entryKey, path);
        if (name === '$item') {
          const list = path.at(-2) ?? entryKey;
          return (roots.item === undefined ? undefined : copy(`${roots.item}.${list}`)) ?? i18n.t('settings.control.item', { ns: 'console' });
        }
        // A property's copy can depend on what it is inside: a holiday's `name` is not the venue's.
        const parent = path.length === 1 ? copyId(entryKey) : path.at(-2);
        return (
          (parent === undefined ? undefined : copy(`${roots.field}.${parent}__${name}`)) ??
          copy(`${roots.field}.${name}`) ??
          humanise(name)
        );
      },
      description(entryKey, path, schema: JsonSchema) {
        if (roots.description === undefined) {
          return path.length === 0 || typeof schema['description'] !== 'string' ? undefined : schema['description'];
        }
        return copy(`${roots.description}.${last(entryKey, path)}`);
      },
      option(entryKey, path, value) {
        const owner = path.length === 0 ? copyId(entryKey) : last(entryKey, path);
        return copy(`${roots.option}.${owner}.${String(value)}`) ?? readable(String(value));
      },
    };
  }, [t, i18n, ns, roots.entry, roots.field, roots.item, roots.description, roots.option]);
}

/** The registry's copy for setting labels (`settings.label.<key>`, `settings.field.<name>`). */
export function useRegistryLabels(): SettingLabels {
  return useSettingLabels({ entry: 'settings.label', field: 'settings.field', option: 'settings.option' });
}

/**
 * The one line of help under a setting's label (`settings.help.<key>`), or undefined when the console has none — the
 * renderer then falls back to the registry's description, which a test keeps from ever shipping that way.
 */
export function useSettingHelp(): (key: string) => string | undefined {
  const { t, i18n } = useTranslation('console');
  return useMemo(() => (key: string) => {
    const id = `settings.help.${copyId(key)}`;
    return i18n.exists(id, { ns: 'console' }) ? t(id) : undefined;
  }, [t, i18n]);
}

/** The words of a setting's choices, for search: "Block — recommended Read only … Allow". */
export function optionWords(entry: { readonly key: string; readonly schema: Readonly<Record<string, unknown>> }, labels: SettingLabels): string {
  const values = enumValues(entry.schema);
  return values.map((value) => labels.option(entry.key, [], value)).join(' ');
}

function enumValues(schema: Readonly<Record<string, unknown>>): (string | number | boolean)[] {
  const direct = schema['enum'];
  if (Array.isArray(direct)) return direct.filter((value): value is string | number | boolean => ['string', 'number', 'boolean'].includes(typeof value));
  const items = schema['items'];
  if (typeof items === 'object' && items !== null) return enumValues(items as Readonly<Record<string, unknown>>);
  const members = schema['anyOf'] ?? schema['oneOf'];
  if (Array.isArray(members)) {
    return members.flatMap((member) =>
      typeof member === 'object' && member !== null && 'const' in member ? [(member as { const: string | number | boolean }).const] : [],
    );
  }
  return [];
}
